import Peer from '@bowbee/peer-lite';
import type {
  IPeerConnection,
  IPeerConnectionFactory,
  PeerConnectionOptions,
  PeerConnectionEvent,
} from './types';
import { TransceiverInitializer, TransceiverSpec } from './TransceiverInitializer';
import { NegotiationWatchdog } from './NegotiationWatchdog';

/**
 * Production wrapper around @bowbee/peer-lite implementing IPeerConnection.
 * Tests inject MockPeerConnectionFactory instead.
 */
class PeerLiteConnection implements IPeerConnection {
  private peer: Peer;
  private transceiverInitializer = new TransceiverInitializer();
  private watchdog = new NegotiationWatchdog();

  constructor(options: PeerConnectionOptions) {
    this.peer = new Peer({
      id: options.id,
      config: options.config,
      batchCandidates: options.batchCandidates ?? true,
      batchCandidatesTimeout: options.batchCandidatesTimeout ?? 1000,
      enableDataChannels: options.enableDataChannels ?? true,
      channelLabel: options.channelLabel ?? 'data',
      ...(options.sdpTransform ? { sdpTransform: options.sdpTransform } : {}),
    });
    this.peer.init();
    const pc = this.safeGetPc();
    if (pc) this.watchdog.attach(pc, (sdp) => this.emitManualOffer(sdp));
  }

  start(options: { polite: boolean }): void {
    this.peer.start({ polite: options.polite });
    this.watchdog.activate();
  }

  async signal(description: RTCSessionDescriptionInit): Promise<void> {
    await this.peer.signal(description);
  }

  async addIceCandidate(candidate: RTCIceCandidate): Promise<void> {
    await this.peer.addIceCandidate(candidate);
  }

  addTrack(track: MediaStreamTrack): void {
    this.peer.addTrack(track);
  }

  removeTrack(track: MediaStreamTrack): void {
    this.peer.removeTrack(track);
  }

  async replaceTrack(oldTrack: MediaStreamTrack, newTrack: MediaStreamTrack): Promise<void> {
    await this.peer.replaceTrack(oldTrack, newTrack);
  }

  addDataChannel(label: string, options?: RTCDataChannelInit): void {
    this.peer.addDataChannel(label, options);
  }

  ensureTransceivers(specs: TransceiverSpec[]): void {
    this.transceiverInitializer.ensure(this.safeGetPc(), specs);
  }

  send(data: string | ArrayBuffer, label?: string): boolean {
    return this.peer.send(data, label);
  }

  destroy(): void {
    this.watchdog.detach();
    this.peer.destroy();
  }

  isConnected(): boolean {
    return this.peer.isConnected();
  }

  getRtcPeerConnection(): RTCPeerConnection | null {
    return this.safeGetPc();
  }

  on(event: PeerConnectionEvent, listener: (...args: unknown[]) => void): void {
    this.peer.on(event as any, listener as any);
  }

  off(event: PeerConnectionEvent, listener: (...args: unknown[]) => void): void {
    this.peer.off(event as any, listener as any);
  }

  async restartIce(): Promise<void> {
    const pc = this.safeGetPc();
    if (!pc) return;
    pc.restartIce();
    const offer = await pc.createOffer({ iceRestart: true });
    await pc.setLocalDescription(offer);
    this.emitManualOffer(pc.localDescription as RTCSessionDescriptionInit);
  }

  private safeGetPc(): RTCPeerConnection | null {
    try {
      return this.peer.get();
    } catch {
      return null;
    }
  }

  private emitManualOffer(sdp: RTCSessionDescriptionInit): void {
    (this.peer as unknown as { emit: (event: string, payload: unknown) => void }).emit(
      'signal',
      sdp,
    );
  }
}

/**
 * Factory that creates real @bowbee/peer-lite instances.
 */
export class PeerLiteConnectionFactory implements IPeerConnectionFactory {
  create(options: PeerConnectionOptions): IPeerConnection {
    return new PeerLiteConnection(options);
  }
}
